/**
 * frontend/src/pages/valuation/_useIntakeInputs.ts
 *
 * VAL-INPUT-PERSIST-1A — split out of ValuationIntake.tsx under
 * FILE_SIZE_POLICY.md ahead of pieces B and D, which both add fields here.
 * No behaviour change: the 16 input `useState`s, their hydration from the
 * working draft (`hydrateFields`, the hydration effect, `hydrated`) and
 * `startClean()` moved verbatim. (VAL-PATH P5 deleted `intakeDraftPatch`:
 * the step footer saves, then the run reads the SAVED draft server-side.)
 */
import { useCallback, useEffect, useRef, useState, type SetStateAction } from 'react'
import type { HouseDefault } from '@/types/valuation'
import type { DraftInputs } from '@/types/valuationDraft'
import { INTAKE_DEFAULTS, type HydrateKey } from './_valuationIntakeHelpers'

/**
 * VAL-DRAFT-OVERWRITE-1 (Intake) — `useState` whose setter records that the
 * user has edited the field. A late draft hydrate fills only fields not in
 * `touched`; `raw` is the screen's own write (hydrate, start clean) and does
 * not count as an edit.
 */
function useField(touched: Set<string>, name: string, init: string) {
  const [value, raw] = useState(init)
  const set = useCallback((v: string) => {
    touched.add(name)
    raw(v)
  }, [touched, name])
  return [value, set, raw] as const
}

export function useIntakeInputs(
  draft: DraftInputs | null,
  draftLoading: boolean,
  dismissDraft: () => void,
  // FIX_VAL-OWNER-INPUTS-2 — server-served house starting values (owner only; null for the signer).
  starting: Record<string, HouseDefault> | null = null,
) {
  // Names of fields the user has typed in (VAL-DRAFT-OVERWRITE-1).
  const touched = useRef(new Set<string>())
  // Income leg
  const [riskFree, setRiskFree, setRiskFreeRaw] = useField(touched.current, 'riskFree', INTAKE_DEFAULTS.riskFree)
  const [equityRiskPremium, setEquityRiskPremium, setEquityRiskPremiumRaw] = useField(touched.current, 'equityRiskPremium', INTAKE_DEFAULTS.equityRiskPremium)
  const [sizePremium, setSizePremium, setSizePremiumRaw] = useField(touched.current, 'sizePremium', INTAKE_DEFAULTS.sizePremium)
  const [companySpecificRisk, setCompanySpecificRisk, setCompanySpecificRiskRaw] = useField(touched.current, 'companySpecificRisk', INTAKE_DEFAULTS.companySpecificRisk)
  const [longTermGrowth, setLongTermGrowth, setLongTermGrowthRaw] = useField(touched.current, 'longTermGrowth', INTAKE_DEFAULTS.longTermGrowth)
  const [normalizedEarnings, setNormalizedEarnings, setNormalizedEarningsRaw] = useField(touched.current, 'normalizedEarnings', '')
  const [baseCashFlow, setBaseCashFlow, setBaseCashFlowRaw] = useField(touched.current, 'baseCashFlow', INTAKE_DEFAULTS.baseCashFlow)
  // VAL-INCOME-BASIS-FIX-1: annual cost to hire a market-rate replacement to
  // run the business. Required by the API (400) below the signed SDE/EBITDA
  // crossover, since SDE carries the owner's full pay; blank is allowed here
  // and produces that 400 on run, not a client-side block.
  const [replacementCompensation, setReplacementCompensation, setReplacementCompensationRaw] = useField(touched.current, 'replacementCompensation', INTAKE_DEFAULTS.replacementCompensation)
  const [growthRate, setGrowthRate, setGrowthRateRaw] = useField(touched.current, 'growthRate', INTAKE_DEFAULTS.growthRate)
  const [projectionYears, setProjectionYears, setProjectionYearsRaw] = useField(touched.current, 'projectionYears', INTAKE_DEFAULTS.projectionYears)
  const [capWeight, setCapWeight, setCapWeightRaw] = useField(touched.current, 'capWeight', INTAKE_DEFAULTS.capWeight)
  // Market leg
  const [compMultiples, setCompMultiples, setCompMultiplesRaw] = useField(touched.current, 'compMultiples', '')
  // Asset floor
  const [totalAssets, setTotalAssets, setTotalAssetsRaw] = useField(touched.current, 'totalAssets', '')
  const [totalLiabilities, setTotalLiabilities, setTotalLiabilitiesRaw] = useField(touched.current, 'totalLiabilities', '')
  const [fairValueAdjustments, setFairValueAdjustments, setFairValueAdjustmentsRaw] = useField(touched.current, 'fairValueAdjustments', '')
  // Signed-methodology sections. `driverAnswers` is `question_id -> option
  // value`; there is no driver-score state on this screen any more, because a
  // score the browser holds is a score the browser can set (guardrail 1).
  const [driverAnswers, setDriverAnswersRaw] = useState<Record<string, string>>({})
  const setDriverAnswers = useCallback(
    (next: SetStateAction<Record<string, string>>) =>
      setDriverAnswersRaw(prev => {
        const n = typeof next === 'function' ? next(prev) : next
        for (const k of Object.keys(n)) if (n[k] !== prev[k]) touched.current.add(`answer:${k}`)
        return n
      }),
    [],
  )
  // VAL-PATH-SIGNER P7b — the multiple override (VAL-INPUT-PERSIST-1D) moved
  // to Methodology and rates (_useMethodologyEdits.ts); Intake no longer holds it.

  const [hydrated, setHydrated] = useState(false)
  // True when the user has typed: autosave reads it only on the render it turns
  // on, where any edit is one the server has not seen, so the payload is not a
  // baseline to skip. FIX65-E: read at that render, not in the hydrate effect —
  // an answer given between the effect and its render was taken as saved.
  const typedBeforeHydrate = touched.current.size > 0

  // VAL-INTAKE-HYDRATE-ALL-1 — the 10 build-up/DCF fields the draft holds,
  // restored by the hydration effect and reset by
  // `startClean()` from this ONE list — no ten copy-pasted lines either place.
  const hydrateFields: Array<[HydrateKey, (v: string) => void, string, string]> = [
    ['risk_free_rate', setRiskFreeRaw, 'riskFree', INTAKE_DEFAULTS.riskFree],
    ['company_specific_risk', setCompanySpecificRiskRaw, 'companySpecificRisk', INTAKE_DEFAULTS.companySpecificRisk],
    ['long_term_growth_rate', setLongTermGrowthRaw, 'longTermGrowth', INTAKE_DEFAULTS.longTermGrowth],
    ['equity_risk_premium', setEquityRiskPremiumRaw, 'equityRiskPremium', INTAKE_DEFAULTS.equityRiskPremium],
    ['size_premium', setSizePremiumRaw, 'sizePremium', INTAKE_DEFAULTS.sizePremium],
    ['growth_rate', setGrowthRateRaw, 'growthRate', INTAKE_DEFAULTS.growthRate],
    ['projection_years', setProjectionYearsRaw, 'projectionYears', INTAKE_DEFAULTS.projectionYears],
    ['capitalized_earnings_weight', setCapWeightRaw, 'capWeight', INTAKE_DEFAULTS.capWeight],
    ['base_cash_flow', setBaseCashFlowRaw, 'baseCashFlow', INTAKE_DEFAULTS.baseCashFlow],
    ['replacement_compensation', setReplacementCompensationRaw, 'replacementCompensation', INTAKE_DEFAULTS.replacementCompensation],
  ]

  // FIX_VAL-OWNER-INPUTS-2 — once the draft has settled AND the defaults have
  // arrived, fill each field the draft left empty and the user has not typed in.
  // The setters are the screen's own write, so autosave then saves the value
  // (attributed); a draft value or a typed value always wins.
  const startingApplied = useRef(false)
  useEffect(() => {
    if (!hydrated || !starting || startingApplied.current) return
    startingApplied.current = true
    for (const [draftKey, setter, name] of hydrateFields) {
      const d = starting[draftKey]
      const saved = draft ? draft[draftKey] : null
      if (d && (saved === null || saved === undefined) && !touched.current.has(name)) setter(String(d.value))
    }
  }, [hydrated, starting]) // eslint-disable-line react-hooks/exhaustive-deps

  // D3 — hydrate once the draft has settled (loaded or confirmed absent).
  // Only the fields this screen owns (asset floor) plus the shared driver
  // answers (D4) — never `benchmark_key`, which this screen has no field for.
  useEffect(() => {
    if (hydrated || draftLoading) return
    // R3.10 — normalized_earnings (Earnings card) hydrates too, or a
    // resumed draft shows this screen's blank field despite having one.
    if (draft) {
      // A field the user has typed in is never replaced by the draft.
      const open = (name: string) => !touched.current.has(name)
      if (draft.normalized_earnings && open('normalizedEarnings')) setNormalizedEarningsRaw(String(draft.normalized_earnings))
      if (draft.total_assets !== null && open('totalAssets')) setTotalAssetsRaw(String(draft.total_assets))
      if (draft.total_liabilities !== null && open('totalLiabilities')) setTotalLiabilitiesRaw(String(draft.total_liabilities))
      if (draft.fair_value_adjustments && open('fairValueAdjustments')) setFairValueAdjustmentsRaw(String(draft.fair_value_adjustments))
      // VAL-INPUT-PERSIST-1B B5 — comps are keyed off `.length`, not presence,
      // because the codec omits the key entirely when empty rather than
      // sending `[]` (`finalize_codec.py:101-102`).
      if (draft.comp_multiples?.length && open('compMultiples')) setCompMultiplesRaw(draft.comp_multiples.join(', '))
      // Driver answers merge per question: the user's own answers win.
      if (Object.keys(draft.answers).length > 0) {
        setDriverAnswersRaw(prev => {
          const merged = { ...draft.answers }
          for (const k of Object.keys(prev)) if (touched.current.has(`answer:${k}`)) merged[k] = prev[k]
          return merged
        })
      }
      // VAL-INTAKE-HYDRATE-ALL-1 — the 10 build-up/DCF fields; a null/undefined
      // draft value keeps the useState default rather than overwriting it.
      for (const [draftKey, setter, name] of hydrateFields) {
        const value: DraftInputs[typeof draftKey] = draft[draftKey]
        if (value !== null && value !== undefined && open(name)) setter(String(value))
      }
    }
    setHydrated(true)
  }, [draft, draftLoading, hydrated])

  /** D3 "start clean" — clears the resumed driver answers back to blank; the draft row itself is untouched (Tuning's half must survive).
   * VAL-PATH-ADJUST P4b — the legs, rates, earnings and asset floor belong to
   * step 3 now, so they keep the draft's values (Run still reads them). */
  function startClean() {
    dismissDraft()
    touched.current.clear()
    setDriverAnswersRaw({})
  }

  return {
    riskFree, setRiskFree,
    equityRiskPremium, setEquityRiskPremium,
    sizePremium, setSizePremium,
    companySpecificRisk, setCompanySpecificRisk,
    longTermGrowth, setLongTermGrowth,
    normalizedEarnings, setNormalizedEarnings,
    baseCashFlow, setBaseCashFlow,
    replacementCompensation, setReplacementCompensation,
    growthRate, setGrowthRate,
    projectionYears, setProjectionYears,
    capWeight, setCapWeight,
    compMultiples, setCompMultiples,
    totalAssets, setTotalAssets,
    totalLiabilities, setTotalLiabilities,
    fairValueAdjustments, setFairValueAdjustments,
    driverAnswers, setDriverAnswers,
    hydrated,
    typedBeforeHydrate,
    startClean,
  }
}
